<template>
  <v-container fluid v-if="selectedMonthId" class="pa-0 h-100">
    <shift-management-month-detail :studio-id="selectedStudio" :month-id="selectedMonthId" @back="closeMonthDetail" />
  </v-container>
  <v-container v-else>
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <h2>Arbeitseinsatz Planung</h2>
        <v-btn color="primary" @click="openDialog('create', $event)"> <v-icon left>mdi-plus</v-icon> Neuen Monat planen </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="availableStudios.length > 1" class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          filled
          dense
          hide-details
          :items="availableStudios"
          label="Studio auswählen"
          v-model="selectedStudio"
          item-text="name"
          item-value="_id"
          @change="fetchMonthlyPlans"></v-select>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12" class="text-center mt-10">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 grey--text">Lade Übersicht...</p>
      </v-col>
    </v-row>

    <v-row v-else-if="monthlyPlans.length === 0">
      <v-col cols="12" class="text-center mt-10">
        <v-card dark class="pa-5 rounded-xl mx-auto" outlined style="max-width: 500px">
          <v-icon size="64" color="grey">mdi-calendar-blank</v-icon>
          <h3 class="mt-4 grey--text">Keine Planungen vorhanden</h3>
          <p class="grey--text">Erstellen Sie den ersten Arbeitsplan für einen neuen Monat.</p>
          <v-btn color="primary" class="mt-4" @click="openDialog('create', $event)">Jetzt planen</v-btn>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12" md="6" lg="4" v-for="plan in formattedPlans" :key="plan._id">
        <v-card dark class="rounded-xl pa-2" hover @click="openMonthDetail(plan._id)">
          <v-card-title class="d-flex justify-space-between align-center">
            <span>{{ plan.monthName }}</span>
            <v-chip :color="plan.isActive ? 'success' : plan.isFuture ? 'primary' : 'grey'" small>
              {{ plan.isActive ? "Aktiv" : plan.isFuture ? "In Planung" : "Abgeschlossen" }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <div class="mb-2"><strong>Zuweisungen:</strong> {{ plan.assignedShifts }} / {{ plan.totalShifts }} Schichten</div>
            <v-progress-linear
              :value="(plan.assignedShifts / plan.totalShifts) * 100"
              :color="plan.assignedShifts === plan.totalShifts ? 'success' : 'warning'"
              height="8"
              rounded></v-progress-linear>
            <div class="mt-3 d-flex justify-space-between align-center">
              <span class="text-caption grey--text text--lighten-1" v-if="plan.unassignedShifts > 0">
                <v-icon x-small color="warning">mdi-alert</v-icon> {{ plan.unassignedShifts }} unbesetzt
              </span>
              <span v-else class="text-caption success--text"> <v-icon x-small color="success">mdi-check-circle</v-icon> Vollständig </span>
              <v-btn small text color="primary">Detailansicht <v-icon right small>mdi-arrow-right</v-icon></v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog-Komponente -->
    <shift-management-main-dialog v-model="dialog" :mode="dialogMode" :origin="dialogOrigin" @input="handleDialogClosed" />
  </v-container>
</template>

<script>
import shiftManagementMainDialog from "./shiftManagementMainDialog.vue"
import shiftManagementMonthDetail from "./shiftManagementMonthDetail.vue"
import api from "@/api"

export default {
  name: "shiftManagementMain",
  components: {
    shiftManagementMainDialog,
    shiftManagementMonthDetail,
  },
  data() {
    return {
      loading: true,
      availableStudios: [],
      selectedStudio: null,
      monthlyPlans: [],
      selectedMonthId: null,
      dialog: false,
      dialogMode: "create",
      dialogOrigin: "center center",
    }
  },
  computed: {
    formattedPlans() {
      if (!this.monthlyPlans) return []
      const currentMonth = this.$moment().format("YYYY-MM")

      return this.monthlyPlans.map((plan) => {
        const momentDate = this.$moment(plan._id, "YYYY-MM")
        return {
          ...plan,
          monthName: momentDate.format("MMMM YYYY"),
          isFuture: plan._id > currentMonth,
          isActive: plan._id === currentMonth,
          isPast: plan._id < currentMonth,
        }
      })
    },
  },
  async created() {
    try {
      this.availableStudios = await api.fetchAllowedAdminStudios()
      if (this.availableStudios.length > 0) {
        this.selectedStudio = this.availableStudios[0]._id
        await this.fetchMonthlyPlans()
      }
    } catch (e) {
      console.error(e)
      this.$toast.error("Fehler beim Laden der Studios.")
    } finally {
      this.loading = false
    }
  },
  methods: {
    async fetchMonthlyPlans() {
      if (!this.selectedStudio) return
      this.loading = true
      try {
        this.monthlyPlans = await api.shiftManagement.fetchPlansOverview(this.selectedStudio)
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Monatsübersicht.")
      } finally {
        this.loading = false
      }
    },
    openDialog(mode, event) {
      this.dialogMode = mode
      const rect = event.currentTarget.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      this.dialogOrigin = `${centerX}px ${centerY}px`
      this.dialog = true
    },
    handleDialogClosed(val) {
      if (!val) {
        // Dialog was closed
        setTimeout(() => {
          this.fetchMonthlyPlans()
        }, 500) // Give the backend a ms to save the final status
      }
    },
    openMonthDetail(monthId) {
      this.selectedMonthId = monthId
    },
    closeMonthDetail() {
      this.selectedMonthId = null
      this.fetchMonthlyPlans() // Aufladen nach Änderungen
    },
  },
}
</script>
